import React, { useCallback, useMemo } from 'react'
import uuidV4 from 'lib/string/uuidV4Utils'
import AlwaysOnRateOptionMobile from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnRateOptionMobile'
import BedbankRoomOptionRate from 'components/OfferPage/Bedbank/BedbankRoomOptionRate'
import PropertyRateOption from '../PropertyRateOption'
import type { RateView } from '../singlePropertyUtils'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import TextLink from 'LuxKit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`
interface Props {
  offer: App.HotelOffer;
  bedbankOffer?: App.BedbankOffer;
  displayRates: Array<RateView>;
  roomFitsCapacity: boolean;
  datesAvailable: boolean;
  flightDestination?: App.Airport;
  isHighlighted?: boolean;
  roomRateUnmatched?: boolean;
  roomListId: string;
}

function PropertyRoomOptionMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    displayRates,
    roomFitsCapacity,
    datesAvailable,
    flightDestination,
    isHighlighted,
    roomRateUnmatched,
    roomListId,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const dispatch = useOfferPageDispatch()

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayRates]) // regenerate id if packages change

  const handleChangeCapacityClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [dispatch])

  const handleChangeDatesClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [dispatch])

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        {!displayRates.length && <OptionsContainer>
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            {!roomFitsCapacity && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room cannot accomodate your number of guests"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeCapacityClick}>add more rooms</TextLink></>}
            />}
            {!datesAvailable && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room is sold out on your dates or max number of guests exceeded"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink></>}
            />}
          </Group>
        </OptionsContainer>}
        {displayRates.length > 1 && (
          <OptionsContainer>
            <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', displayRates.length)} available</BodyText>
          </OptionsContainer>
        )}
        <div>
          {isHighlighted && <MessageBanner
            kind="promotional-primary"
            description={<b>You were interested in this option</b>}
            icon={!roomRateUnmatched && <LineSightseeingIcon />}
          />}
          <Group direction="vertical" gap={8}>
            {displayRates.map((view, index) => {
              if (view.propertyRate) {
                return <Pane key={view.propertyRate.rateId}>
                  <PropertyRateOption
                    lpcOffer={offer}
                    bedbankOffer={bedbankOffer}
                    rate={view.propertyRate}
                    optionNumber={index + 1}
                    roomListId={roomListId}
                    roomPosition={index + 1}
                    rateListId={rateListId}
                  />
                </Pane>
              } else if (view.lpcRate) {
                return <Pane key={view.lpcRate.package.uniqueKey}>
                  <AlwaysOnRateOptionMobile
                    offer={offer}
                    packageView={view.lpcRate}
                    flightDestination={flightDestination}
                  />
                </Pane>
              } else if (view.bedbankRate && bedbankOffer) {
                return <Pane key={view.bedbankRate.rate.id}>
                  <BedbankRoomOptionRate
                    offer={bedbankOffer}
                    packageView={view.bedbankRate.view}
                    rate={view.bedbankRate.rate}
                    optionNumber={index + 1}
                    roomPosition={index + 1}
                    roomListId={roomListId}
                    rateListId={rateListId}
                    showOptionLabel={displayRates.length > 1}
                  />
                </Pane>
              }
              return null
            })}
          </Group>
        </div>
      </Group>
    </Pane>
  )
}

export default React.memo(PropertyRoomOptionMobile)
